'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/pro-regular-svg-icons';
import { faClose } from '@fortawesome/pro-solid-svg-icons';
import { Tooltip } from 'src/components/PulseUI/Tooltip';
import useIsAdvancedSearchActive from 'src/hooks/discovery/useIsAdvancedSearchActive';
import { useSavedVolume } from 'src/hooks/savedVolume/useSavedVolume';
import { LOCATION } from 'src/hooks/filters/constants';
import { useGetAdvancedSearchQuery } from 'src/api/redux';
import { HighlightedCriteria } from 'src/components/Strategy/shared';
import CodeSetPreview from 'src/components/CodeSetPreview/CodeSetPreview';
import type { AdvancedSearchDetail } from 'src/api/redux/advancedSearches/types';

interface AdvancedSearchPreviewContentProps {
  label?: string;
  detail?: AdvancedSearchDetail;
  isLoading: boolean;
  onClose: () => void;
}

const AdvancedSearchPreviewContent = ({
  label,
  detail,
  isLoading,
  onClose,
}: AdvancedSearchPreviewContentProps) => {
  const criteriaTemplate = detail?.template?.criteria_template;
  const criteriaInputs = detail?.template?.criteria_inputs;

  return (
    <div className="w-full" data-testid="advanced-search-preview">
      <div className="flex justify-between items-center flex-row pb-2">
        <div
          className="text-primary text-sm font-semibold font-['Inter'] leading-normal"
          data-testid="advanced-search-preview-title"
        >
          {label || 'AI Search Criteria'}
        </div>
        <button
          type="button"
          className="cursor-pointer border-0 bg-transparent p-0"
          onClick={onClose}
          aria-label="Close AI search preview"
          data-testid="advanced-search-preview-close"
        >
          <Icon icon={faClose} size="sm" className="w-4 h-4 text-tertiary" />
        </button>
      </div>
      <div
        aria-live="polite"
        aria-atomic="true"
        data-testid="advanced-search-preview-content"
      >
        {isLoading ? (
          <span className="text-secondary text-sm">Loading...</span>
        ) : criteriaTemplate && criteriaInputs ? (
          <HighlightedCriteria
            criteriaTemplate={criteriaTemplate}
            criteriaInputs={criteriaInputs}
          />
        ) : (
          <span className="text-secondary text-sm">No criteria available</span>
        )}
      </div>
    </div>
  );
};

const CodePreview = () => {
  const isAdvancedSearchActive = useIsAdvancedSearchActive();
  const { appliedSavedVolume } = useSavedVolume(LOCATION.DISCOVERY);

  const advancedSearchId = isAdvancedSearchActive
    ? appliedSavedVolume?.id
    : undefined;

  const {
    data: advancedSearchDetail,
    isLoading: isAdvSearchLoading,
    isFetching: isAdvSearchFetching,
  } = useGetAdvancedSearchQuery(advancedSearchId, {
    skip: !advancedSearchId,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    right: 0,
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Ensure component is mounted before rendering portal
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Update dropdown position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isOpen]);

  // Handle click outside, resize, and scroll to close dropdown
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInDropdown = dropdownRef.current?.contains(target);
      const clickedOnButton = buttonRef.current?.contains(target);

      if (!clickedInDropdown && !clickedOnButton) {
        setIsOpen(false);
      }
    };

    const handleClose = () => setIsOpen(false);

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleClose);
    window.addEventListener('scroll', handleClose, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleClose);
      window.removeEventListener('scroll', handleClose, true);
    };
  }, [isOpen]);

  const toggleDropdown = () => {
    // Calculate position before opening to avoid flash
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setIsOpen(!isOpen);
  };

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const dropdownContent =
    isOpen && isMounted
      ? createPortal(
          <div
            ref={dropdownRef}
            role="dialog"
            aria-label={
              isAdvancedSearchActive
                ? 'AI search criteria'
                : 'Code set preview'
            }
            aria-modal="true"
            data-testid="code-preview-dropdown"
            className="fixed min-w-72 max-w-md bg-white border border-neutral-300 rounded-lg shadow-lg z-[9999] p-4"
            style={{ top: dropdownPosition.top, right: dropdownPosition.right }}
          >
            {isAdvancedSearchActive ? (
              <AdvancedSearchPreviewContent
                label={appliedSavedVolume?.label}
                detail={advancedSearchDetail}
                isLoading={isAdvSearchLoading || isAdvSearchFetching}
                onClose={handleClose}
              />
            ) : (
              <CodeSetPreview
                location={LOCATION.DISCOVERY}
                onClose={handleClose}
              />
            )}
          </div>,
          document.body
        )
      : null;

  return (
    <div className="relative flex justify-end">
      <Tooltip content="Preview search" position="top">
        <button
          ref={buttonRef}
          type="button"
          aria-label="Preview search"
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          data-testid="code-preview-button"
          className="flex justify-center items-center border border-gray-300 rounded-md bg-white h-9 w-9"
          onClick={toggleDropdown}
        >
          <Icon icon={faEye} className="w-5 h-5 text-sky-600" aria-hidden />
        </button>
      </Tooltip>
      {dropdownContent}
    </div>
  );
};

export default CodePreview;
